/**
 * Invoice Translations for Frontend
 * Mirrors backend/services/invoice_translations.py for live preview
 * Supported languages: English (en), Polish (pl), Romanian (ro)
 */

import { InvoiceLanguage } from '../types';

export type TranslationKey =
  | 'invoice' | 'invoice_vat' | 'invoice_reverse_charge' | 'proforma' | 'credit_note'
  | 'invoice_number' | 'invoice_date' | 'sale_date' | 'due_date' | 'place_of_issue'
  | 'from' | 'seller' | 'bill_to' | 'buyer'
  | 'tax_id' | 'vat_id' | 'email' | 'phone'
  | 'items' | 'description' | 'quantity' | 'unit' | 'unit_price' | 'tax_rate' | 'amount' | 'total'
  | 'subtotal' | 'tax' | 'discount' | 'total_due' | 'amount_paid' | 'balance_due'
  | 'payment_info' | 'bank_details' | 'bank_name' | 'account_number' | 'iban' | 'swift' | 'payment_method' | 'payment_terms'
  | 'notes' | 'terms'
  | 'currency' | 'summary' | 'invoice_type' | 'type' | 'language' | 'primary_language' | 'secondary_language'
  | 'invoice_details' | 'additional_info' | 'client' | 'add_item' | 'create_invoice' | 'update_invoice' | 'select_seller' | 'select_client'
  | 'reverse_charge_notice' | 'proforma_notice' | 'credit_note_reference'
  | 'tax_exempt';

type Translations = {
  [key in TranslationKey]: string;
};

type TranslationMap = {
  [lang in InvoiceLanguage]: Translations;
};

export const TRANSLATIONS: TranslationMap = {
  en: {
    // Document types
    invoice: 'INVOICE',
    invoice_vat: 'VAT INVOICE',
    invoice_reverse_charge: 'INVOICE REVERSE CHARGE VAT',
    proforma: 'PROFORMA INVOICE',
    credit_note: 'CREDIT NOTE',

    // Header labels
    invoice_number: 'Invoice Number',
    invoice_date: 'Invoice Date',
    sale_date: 'Sale Date',
    due_date: 'Due Date',
    place_of_issue: 'Place of Issue',

    // Parties
    from: 'From',
    seller: 'Seller',
    bill_to: 'Bill To',
    buyer: 'Buyer',
    client: 'Client',

    // Contact info
    tax_id: 'Tax ID',
    vat_id: 'VAT ID',
    email: 'Email',
    phone: 'Phone',

    // Items table
    items: 'Items',
    description: 'Description',
    quantity: 'Qty',
    unit: 'Unit',
    unit_price: 'Unit Price',
    tax_rate: 'Tax Rate',
    amount: 'Amount',
    total: 'Total',

    // Totals
    subtotal: 'Subtotal',
    tax: 'Tax',
    discount: 'Discount',
    total_due: 'Total Due',
    amount_paid: 'Amount Paid',
    balance_due: 'Balance Due',

    // Payment info
    payment_info: 'Payment Information',
    bank_details: 'Bank Details',
    bank_name: 'Bank',
    account_number: 'Account Number',
    iban: 'IBAN',
    swift: 'SWIFT/BIC',
    payment_method: 'Payment Method',
    payment_terms: 'Payment Terms',

    // Additional sections
    notes: 'Notes',
    terms: 'Terms & Conditions',

    // Form labels
    currency: 'Currency',
    summary: 'Summary',
    invoice_type: 'Invoice Type',
    type: 'Type',
    language: 'Language',
    primary_language: 'Primary Language',
    secondary_language: 'Secondary Language (Bilingual)',
    invoice_details: 'Invoice Details',
    additional_info: 'Additional Information',
    add_item: 'Add Item',
    create_invoice: 'Create Invoice',
    update_invoice: 'Update Invoice',
    select_seller: 'Select seller profile',
    select_client: 'Select a client',

    // Legal notices
    reverse_charge_notice: 'Reverse charge - VAT to be accounted for by the recipient as per Article 196 of the VAT Directive',
    proforma_notice: 'This is a proforma invoice and is not a request for payment',
    credit_note_reference: 'Reference to Invoice',
    tax_exempt: 'Exempt',
  },

  pl: {
    // Document types
    invoice: 'FAKTURA',
    invoice_vat: 'FAKTURA VAT',
    invoice_reverse_charge: 'FAKTURA ODWROTNE OBCIĄŻENIE VAT',
    proforma: 'FAKTURA PROFORMA',
    credit_note: 'FAKTURA KORYGUJĄCA',

    // Header labels
    invoice_number: 'Numer faktury',
    invoice_date: 'Data wystawienia',
    sale_date: 'Data sprzedaży',
    due_date: 'Termin płatności',
    place_of_issue: 'Miejsce wystawienia',

    // Parties
    from: 'Od',
    seller: 'Sprzedawca',
    bill_to: 'Nabywca',
    buyer: 'Nabywca',
    client: 'Klient',

    // Contact info
    tax_id: 'NIP',
    vat_id: 'NIP UE',
    email: 'E-mail',
    phone: 'Telefon',

    // Items table
    items: 'Pozycje',
    description: 'Opis',
    quantity: 'Ilość',
    unit: 'J.m.',
    unit_price: 'Cena jedn.',
    tax_rate: 'Stawka VAT',
    amount: 'Wartość',
    total: 'Razem',

    // Totals
    subtotal: 'Wartość netto',
    tax: 'VAT',
    discount: 'Rabat',
    total_due: 'Do zapłaty',
    amount_paid: 'Zapłacono',
    balance_due: 'Pozostało do zapłaty',

    // Payment info
    payment_info: 'Informacje o płatności',
    bank_details: 'Dane bankowe',
    bank_name: 'Bank',
    account_number: 'Numer konta',
    iban: 'IBAN',
    swift: 'SWIFT/BIC',
    payment_method: 'Forma płatności',
    payment_terms: 'Warunki płatności',

    // Additional sections
    notes: 'Uwagi',
    terms: 'Warunki',

    // Form labels
    currency: 'Waluta',
    summary: 'Podsumowanie',
    invoice_type: 'Typ faktury',
    type: 'Typ',
    language: 'Język',
    primary_language: 'Język główny',
    secondary_language: 'Drugi język (dwujęzyczna)',
    invoice_details: 'Szczegóły faktury',
    additional_info: 'Dodatkowe informacje',
    add_item: 'Dodaj pozycję',
    create_invoice: 'Utwórz fakturę',
    update_invoice: 'Aktualizuj fakturę',
    select_seller: 'Wybierz profil sprzedawcy',
    select_client: 'Wybierz klienta',

    // Legal notices
    reverse_charge_notice: 'Odwrotne obciążenie - VAT rozlicza nabywca zgodnie z art. 17 ust. 1 pkt 7 lub 8 ustawy o VAT',
    proforma_notice: 'To jest faktura proforma i nie stanowi wezwania do zapłaty',
    credit_note_reference: 'Korekta do faktury',
    tax_exempt: 'zw.',
  },

  ro: {
    // Document types
    invoice: 'FACTURĂ',
    invoice_vat: 'FACTURĂ TVA',
    invoice_reverse_charge: 'FACTURĂ TAXARE INVERSĂ TVA',
    proforma: 'FACTURĂ PROFORMA',
    credit_note: 'FACTURĂ DE CORECȚIE',

    // Header labels
    invoice_number: 'Număr factură',
    invoice_date: 'Data facturii',
    sale_date: 'Data vânzării',
    due_date: 'Data scadenței',
    place_of_issue: 'Locul emiterii',

    // Parties
    from: 'De la',
    seller: 'Furnizor',
    bill_to: 'Către',
    buyer: 'Cumpărător',
    client: 'Client',

    // Contact info
    tax_id: 'CUI',
    vat_id: 'Cod TVA',
    email: 'E-mail',
    phone: 'Telefon',

    // Items table
    items: 'Articole',
    description: 'Descriere',
    quantity: 'Cant.',
    unit: 'U.M.',
    unit_price: 'Preț unitar',
    tax_rate: 'Cotă TVA',
    amount: 'Valoare',
    total: 'Total',

    // Totals
    subtotal: 'Subtotal',
    tax: 'TVA',
    discount: 'Reducere',
    total_due: 'Total de plată',
    amount_paid: 'Sumă plătită',
    balance_due: 'Rest de plată',

    // Payment info
    payment_info: 'Informații de plată',
    bank_details: 'Date bancare',
    bank_name: 'Banca',
    account_number: 'Număr cont',
    iban: 'IBAN',
    swift: 'SWIFT/BIC',
    payment_method: 'Metodă de plată',
    payment_terms: 'Termeni de plată',

    // Additional sections
    notes: 'Note',
    terms: 'Termeni și condiții',

    // Form labels
    currency: 'Moneda',
    summary: 'Sumar',
    invoice_type: 'Tip factură',
    type: 'Tip',
    language: 'Limba',
    primary_language: 'Limba principală',
    secondary_language: 'Limba secundară (bilingv)',
    invoice_details: 'Detalii factură',
    additional_info: 'Informații suplimentare',
    add_item: 'Adaugă articol',
    create_invoice: 'Creează factură',
    update_invoice: 'Actualizează factură',
    select_seller: 'Selectează profil furnizor',
    select_client: 'Selectează un client',

    // Legal notices
    reverse_charge_notice: 'Taxare inversă - TVA se datorează de beneficiar conform art. 160 din Codul Fiscal',
    proforma_notice: 'Aceasta este o factură proforma și nu constituie o cerere de plată',
    credit_note_reference: 'Referință la factura',
    tax_exempt: 'Scutit',
  },
};

/**
 * Get translation for a key in the specified language
 */
export function getTranslation(key: TranslationKey, language: InvoiceLanguage = 'en'): string {
  return TRANSLATIONS[language]?.[key] ?? TRANSLATIONS.en[key] ?? key;
}

/**
 * Get bilingual translation (Primary / Secondary format)
 */
export function getBilingualTranslation(
  key: TranslationKey,
  primaryLang: InvoiceLanguage = 'en',
  secondaryLang?: InvoiceLanguage | ''
): string {
  const primary = getTranslation(key, primaryLang);

  if (secondaryLang && secondaryLang !== primaryLang) {
    const secondary = getTranslation(key, secondaryLang);
    if (primary !== secondary) {
      return `${primary} / ${secondary}`;
    }
  }

  return primary;
}

/**
 * Get invoice type title based on type and language
 */
export function getInvoiceTypeTitle(
  invoiceType: string,
  primaryLang: InvoiceLanguage = 'en',
  secondaryLang?: InvoiceLanguage | ''
): string {
  const typeToKey: { [key: string]: TranslationKey } = {
    standard: 'invoice',
    vat: 'invoice_vat',
    reverse_charge: 'invoice_reverse_charge',
    proforma: 'proforma',
    credit_note: 'credit_note',
  };

  const key = typeToKey[invoiceType] || 'invoice';
  return getBilingualTranslation(key, primaryLang, secondaryLang);
}

/**
 * Hook-like function to get a translator for specific languages
 */
export function createTranslator(primaryLang: InvoiceLanguage, secondaryLang?: InvoiceLanguage | '') {
  return {
    t: (key: TranslationKey) => getBilingualTranslation(key, primaryLang, secondaryLang),
    primary: (key: TranslationKey) => getTranslation(key, primaryLang),
    title: (invoiceType: string) => getInvoiceTypeTitle(invoiceType, primaryLang, secondaryLang),
  };
}
